import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Input, Select, Tabs, Image, message, ConfigProvider } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
  INVENTORY_RECIPE_TYPE,
  INVENTORY_ADUSTMENT_TYPE_UNIT,
  INVENTORY_VARIANT_TABS_BGCOLORS,
  INVENTORY_INGREDIENT_UNIT,
} from "../../../constant/constants";
import useUserStore from "../../../stores/store";
import ApiService from "../../../utils/ApiService";
import { convertBase64ToBlob } from "../../../utils/Common";
import {
  validateRecipeUpsert,
  getUpsertFormData,
  getFinalRecipeVariants,
  defaultVariantList,
  defaultAddOns,
  populateCategoryVariants,
  getFormattedAddOns,
  readyFoodTypes,
} from "../../../services/inventory/recipe.service";
import RecipeFoodAddOnsModal from "./RecipeFoodAddOnsModal";
import VariantInput from "./VariantInput";
import RecipeVariantContainer from "./RecipeVariantContainer";
import RecipeEditExceptionModal from "./RecipeEditExceptionModal";
import CustomLoader from "../../menu/CustomLoader";
import ImageUploadWithCropAndWebp from "../../common/ImageUploadWithCropAndWebp";
import CustomInputNumber from "../../CustomInputNumber";
import FoodMealAddition from "../../restaurants/foods/FoodMealAddition";
import FoodTaxAndDiscount from "../../restaurants/foods/FoodTaxAndDiscount";

export default function RecipeAddEditComponent({ data = {}, currentSelectedVarId = "" }) {
  const { recipeType, name, category, details, image, options, addOns, discount, isDiscountInPercent, readyFoodType, unit } = data;
  const { restaurantId, user, showNotification, setNetworkCall } = useUserStore();
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const [variantRecipeType, setVariantRecipeType] = useState(
    Boolean(recipeType) > 0 ? recipeType : INVENTORY_RECIPE_TYPE[0].value
  );
  const [recipeName, setRecipeName] = useState(name || "");
  const [allCategory, setAllCategory] = useState([]);
  const [menuCategory, setMenuCategory] = useState(category?.id || "");
  const [recipeReadyFoodType, setRecipeReadyFoodType] = useState(readyFoodType || 'RECIPE');
  const [ingredientUnit, setIngredientUnit] = useState(unit || '');
  const [menuCategoryOptions, setMenuCategoryOptions] = useState();
  const [recipeFoodDescription, setRecipeFoodDescription] = useState(details || "");
  const [images, setImages] = useState(Boolean(image) ? [{ url: image }] : []);
  const [recipeVariants, setRecipeVariants] = useState(defaultVariantList);
  const [variantDiscount, setVariantDiscount] = useState(discount || 0);
  const [discountType, setDiscountType] = useState(
    isDiscountInPercent === "Yes" ? INVENTORY_ADUSTMENT_TYPE_UNIT[1].value : INVENTORY_ADUSTMENT_TYPE_UNIT[0].value
  );
  const [isDiscountPercent, setIsDiscountPercent] = useState(isDiscountInPercent || "No");
  const [openAddOnsModal, setOpenAddOnsModal] = useState(false);
  const [variantAddOns, setVariantAddOns] = useState(defaultAddOns);
  const [selectedAddOns, setSelectedAddOns] = useState(getFormattedAddOns(addOns) || []);
  const [ingredientItems, setIngredientItems] = useState([]);
  const [ingredientDropdown, setIngredientDropdown] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(defaultVariantList[0].tempVarId || "");
  const [selectedCollapseKey, setSelectedCollapseKey] = useState("");
  const variantSectionRef = useRef(null);
  const [dependentOrders, setDependentOrders] = useState([]);
  const [dependentSetmenus, setDependentSetmenus] = useState([]);
  const [dependentSetmenuOrders, setDependentSetmenuOrders] = useState([]);
  const [openEditExceptionModal, setOpenEditExcepModal] = useState(false);
  const [newImage, setNewImage] = useState(null);

  const [foodAdditionGroups, setFoodAdditionGroups] = useState({ additionGroups: data.additionGroups });

  const [isVatApplicable, setIsVatApplicable] = useState(data.isVatApplicable ?? true);
  const [isDiscountApplicable, setIsDiscountApplicable] = useState(data.isDiscountApplicable ?? true);
  const [isSupplementaryDutyApplicable, setIsSupplementaryDutyApplicable] = useState(data.isSupplementaryDutyApplicable ?? false);
  const [supplementaryDuty, setSupplementaryDuty] = useState(data.supplementaryDuty ?? "");

  function onHandleAddVariant() {
    if (!isCategorySelected()) return;
    const tempVariant = {
      tempVarId: crypto.randomUUID(),
      tempCollapseKey: crypto.randomUUID(),
      optionId: "",
      status: "Active",
      isSelected: false,
      name: "",
      ingredientCost: 0,
      operationalCost: 0,
      discountAmount: 0,
      discountedPrice: 0,
      totalCost: 0,
      profitAmount: "",
      profitPercent: "",
      isProfitInPercent: "No",
      sellingPrice: 0,
      ingredients: [
        {
          tempIngredientId: crypto.randomUUID(),
          ingredientId: "",
          ingredientName: "",
          quantity: 1,
          cost: "",
          baseUnit: "",
          recipeUnit: "",
          recipeUnitPrice: "",
          ingredientTotalCost: 0,
        },
      ],
    };
    setRecipeVariants((prevVariants) => [...prevVariants, { ...tempVariant }]);

    /** When we add new variant from add button
     * by default it will select the newly added variants tab-right section
     * and the left collapse will be opened
     */
    setSelectedVariantId(tempVariant.tempVarId);
    setSelectedCollapseKey(tempVariant.tempCollapseKey);
  }

  function onHandleRecipeType(val) {
    if (Boolean(recipeId)) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Sorry you can not change <span className="text-red-500"> recipe type </span> while you edit recipe.
        </p>
      );
      return;
    }
    setRecipeVariants(defaultVariantList);
    setSelectedVariantId(defaultVariantList[0].tempVarId);
    setRecipeName("");
    setMenuCategory("");
    setVariantRecipeType(val);
  }

  function onHandleRecipeName(e) {
    const name = e.target.value;
    if (name.length > 100) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          You have reached your limit! Please make <span className="text-red-500"> recipe name</span> less than
          <span className="text-red-500"> 100 character</span>.
        </p>
      );
      return;
    }
    setRecipeName(name);
  }

  function onHandleMenuCategory(val) {
    if (!Boolean(variantRecipeType)) {
      message.warning("Please select recipe type first!");
      return;
    }
    disableMouseClick("auto");
    setMenuCategory(val);
  }

  function onHandleRecipeFoodDescription(e) {
    const description = e.target.value;
    if (description.length > 200) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          You have reached your limit! Please make <span className="text-red-500"> recipe food description</span> less than
          <span className="text-red-500"> 200 character</span>.
        </p>
      );
      return;
    }
    setRecipeFoodDescription(description);
  }

  console.log('_____recipeFoodDescription_____', recipeFoodDescription);

  function onHandleEditExceptionModalOkBtn() {
    setOpenEditExcepModal(false);
  }

  function onHandleEditExceptionModalCancelBtn() {
    setOpenEditExcepModal(false);
  }

  async function upsertRecipe(params) {
    setNetworkCall(true);
    try {
      const res = await ApiService.INVENTORY_RECIPE_UPSERT(params, user.token.token);
      const { code, payload } = res;
      if (code !== 200) {
        setNetworkCall(false);
        showNotification(res.data.message || "Something went wrong. Please try again later!");
        return;
      }

      /** if recipe has dependency then success will be success = false */
      if (!Boolean(payload.success)) {
        const { dependentOrders, dependentSetMenuOrders, dependentSetMenus } = payload;
        if (dependentOrders.length > 0 || dependentSetMenuOrders.length > 0 || dependentSetMenus.length > 0) {
          // handleEditDependency(dependentOrders, dependentSetMenuOrders, dependentSetMenus)
          setDependentOrders([...dependentOrders]);
          setDependentSetmenuOrders([...dependentSetMenuOrders]);
          setDependentSetmenus([...dependentSetMenus]);
          setOpenEditExcepModal(true);
        }
        setNetworkCall(false);
        return;
      }

      if (!Boolean(recipeId)) {
        showNotification("Recipe created successfully.", false);
      } else {
        showNotification("Recipe updated successfully.", false);
      }

      setNetworkCall(false);
      return variantRecipeType === INVENTORY_RECIPE_TYPE[1].value
        ? navigate(`/inventory/recipe?recipeType=${variantRecipeType}`)
        : navigate("/inventory/recipe");
    } catch (err) {
      setNetworkCall(false);
      showNotification(err.response?.data.message || "Something went wrong. Please try again later!");
      return;
    }
  }

  /** ingredients dropdowns */
  const getIngredientItems = useCallback(async () => {
    const params = {
      restaurantId: restaurantId,
      recipeId: recipeId || ''
    };
    try {
      const res = await ApiService.GET_INVENTORY_INGREDIENT_DROPDOWN(params, user.token.token);
      if (res.code !== 200) {
        showNotification(res?.data?.message || "Something went wrong. Please try again later!");
        return;
      }
      const { payload } = res;
      const { records } = payload;
      setIngredientItems(records);
      setIngredientDropdown([...records.map((item) => ({ label: item.name, value: item.id }))]);
    } catch (err) {
      showNotification(err?.response?.data?.message || "Something went wrong. Please try again later!");
      return;
    }
  }, [restaurantId, user, showNotification]);

  /** addOns */
  const getAddOns = useCallback(async () => {
    const params = {
      restaurantId: restaurantId,
    };
    try {
      const res = await ApiService.GET_INVENTORY_ADDONS_DROPDOWN(params, user.token.token);

      if (!res || (res && res.code !== 200)) {
        showNotification(res?.data?.message || "Something went wrong. Please try again later!");
        return;
      }
      const { payload } = res;
      const { records } = payload;

      if (Boolean(addOns) && addOns.length > 0) {
        const updatedAddOns = records.map((el) => {
          const found = addOns.find((prevAddOn) => el.addOnId === prevAddOn.addOnId);
          return found ? { ...found, isSelected: true } : { ...el, isSelected: false };
        });
        setVariantAddOns([...updatedAddOns]);
        return;
      }

      setVariantAddOns(records.map((el) => ({ ...el, isSelected: false })));
    } catch (error) {
      showNotification(error?.response?.data?.message || "Something went wrong. Please try again later!");
      return;
    }
  }, [restaurantId, user, showNotification, addOns]);

  const getCategories = useCallback(async () => {
    const params = {
      restaurantId: restaurantId,
    };
    try {
      const res = await ApiService.GET_CATEGORY(params, user.token.token);

      if (!res || (res && res.code !== 200)) {
        showNotification(res?.data?.message || "Something went wrong. Please try again later!");
        return;
      }
      const { records } = res.payload;
      if (!records || records.length === 0) {
        showNotification("No categories found. Please add category first!");
        return;
      }
      setAllCategory(records);
      const tempCategoryOptions = records.map((item) => ({
        label: item.name,
        value: item.id,
      }));

      setMenuCategoryOptions(tempCategoryOptions);
    } catch (error) {
      showNotification(error?.response?.data?.message || "Something went wrong. Please try again later!");
    }
  }, [restaurantId, user, showNotification]);

  function getCategoryOptionsWithId(categoryId, allCategory, recipe) {
    if (!categoryId) return;
    if (allCategory.length < 1) return;

    /** For recipe edit purpose */
    if (Boolean(recipe) && Object.keys(recipe).length > 0 && recipe.category.id === categoryId) {
      const modifiedOptions = [
        ...options.map((option, idx) => ({
          ...option,
          isSelected: true,
          tempVarId: crypto.randomUUID(),
          tempCollapseKey: crypto.randomUUID(),
          isProfitInPercent: option.isProfitInPercent,
          ingredients: [
            ...option.ingredients.map((ingre) => ({
              ...ingre,
              tempIngredientId: crypto.randomUUID(),
            })),
          ],
        })),
      ];
      setRecipeVariants(modifiedOptions);
      const selectedVariantTempId = modifiedOptions.find((el) => el.optionId === currentSelectedVarId)?.tempVarId;
      setSelectedVariantId(selectedVariantTempId);
      return;
    }

    /** For recipe create purpose */
    const categoryOptions = allCategory.find((el) => el.id === categoryId)?.options || [];
    const modifiedVariants = populateCategoryVariants(categoryOptions);

    setRecipeVariants(modifiedVariants);
    setSelectedVariantId(modifiedVariants[0].tempVarId);
  }

  function onHandleUpsert() {
    /**
     * "getFinalRecipeVariants" => function modify user inputed recipe variants for payload
     * "recipeVariants" is user inputed variants array
     * "Boolean(recipeId)" determines @forEdit is a flag variable value
     */
    const selectedRecipeVariants = getFinalRecipeVariants(recipeVariants, Boolean(recipeId));
    const foodOptions = { options: selectedRecipeVariants };

    if (
      !validateRecipeUpsert(
        variantRecipeType,
        recipeName,
        menuCategory,
        recipeVariants,
        selectedRecipeVariants,
        ingredientItems,
        recipeReadyFoodType,
        ingredientUnit
      )
    ) {
      return;
    }

    const upsertAddOns = variantAddOns.filter((item) => item.isSelected).map((item) => item.addOnId);

    let payload = {
      recipeId: Boolean(recipeId) ? recipeId : "",
      restaurantId: restaurantId,
      recipeType: variantRecipeType,
      categoryId: menuCategory,
      name: recipeName.trim(),
      readyFoodType: recipeReadyFoodType,
      unit: ingredientUnit,
      discount: variantDiscount,
      isDiscountInPercent: discountType === INVENTORY_ADUSTMENT_TYPE_UNIT[1].value ? "Yes" : "No",
      details: recipeFoodDescription.trim(),
      hasMultipleOptions: selectedRecipeVariants.length > 1 ? "Yes" : "No",
      foodOptions: foodOptions,
      addOns: recipeType === "ADD_ONS" ? "" : upsertAddOns.length > 0 ? upsertAddOns : "",
      foodAdditionGroups: variantRecipeType === "FOOD" ? JSON.stringify(foodAdditionGroups) : [],

      isVatApplicable: variantRecipeType === "FOOD" ? isVatApplicable ? "YES" : "NO" : "",
      isDiscountApplicable: variantRecipeType === "FOOD" ? isDiscountApplicable ? "YES" : "NO" : "",
      isSupplementaryDutyApplicable: variantRecipeType === "FOOD" ? isSupplementaryDutyApplicable ? "YES" : "NO" : "",
      supplementaryDuty: variantRecipeType === "FOOD" ? isSupplementaryDutyApplicable ? supplementaryDuty : 0 : 0,
    };

    if (images[0]?.hasOwnProperty("thumbUrl")) {
      const recipeUploadedImage = convertBase64ToBlob(images[0]?.thumbUrl, "recipeImage");
      payload = {
        ...payload,
        foodImage: recipeUploadedImage,
      };
    }

    const PAYLOAD_FORM_DATA = getUpsertFormData(payload);
    upsertRecipe(PAYLOAD_FORM_DATA);
  }

  function onHandleDiscount(val) {
    setVariantDiscount(val);
  }

  function onHandleDiscountType(val) {
    let tempIsDiscountPercent = "";
    if (val === INVENTORY_ADUSTMENT_TYPE_UNIT[0].value) {
      tempIsDiscountPercent = "No";
    } else {
      tempIsDiscountPercent = "Yes";
    }
    setVariantDiscount(0);
    setIsDiscountPercent(tempIsDiscountPercent);
    setDiscountType(val);
  }

  function isCategorySelected() {
    if (!Boolean(menuCategory)) {
      // disableMouseClick("none");
      showNotification("Please select category first.");
      return false;
    }
    // disableMouseClick("auto");
    return true;
  }

  function onHandleCheckMenuCategorySelected(e) {
    if (!isCategorySelected()) return;
  }

  function disableMouseClick(pointerType = "auto") {
    if (Boolean(variantSectionRef.current)) {
      variantSectionRef.current.style.pointerEvents = pointerType;
    }
  }

  function onHandleOpenAddOnsModal() {
    setOpenAddOnsModal(true);
  }

  function onHandleAddAddOnsItem() {
    setVariantAddOns((prev) => [
      ...prev,
      {
        addOnId: crypto.randomUUID(),
        name: "",
        price: "",
        isSelected: false,
      },
    ]);
  }

  function onHandleRemoveAddOnsItem() {}

  function updateAddOns(id, fieldName, fieldValue) {
    setVariantAddOns((prev) => prev.map((item) => (item.addOnId === id ? { ...item, [fieldName]: fieldValue } : item)));
  }

  /** Variants addons modal save and cancel button */
  function onHandleSaveAddOns() {
    setOpenAddOnsModal(false);

    if (selectedAddOns.length > 0) {
      selectedAddOns.forEach((addOn) => {
        updateAddOns(addOn.addOnId, "isSelected", addOn.isSelected);
      });
      setSelectedAddOns([]);
    }
  }

  //  show variant names in recipe page
  const selectedAddOnsNames = useMemo(() => {
    return variantAddOns.filter((item) => item.isSelected).map((item) => item.name);
  }, [variantAddOns]);

  function onHandleCancelAddOns() {
    setOpenAddOnsModal(false);

    variantAddOns.forEach((addOn) => {
      updateAddOns(addOn.addOnId, "isSelected", addOn.isSelected);
    });

    setSelectedAddOns([]);
  }
  /** End of Variants addons modal save and cancel button */

  /**
   * This function is for variant tab section
   * here we handle tab changes
   */
  function onHandleTabChange(tempVarId) {
    setSelectedCollapseKey();
    setSelectedVariantId((prev) => (prev === tempVarId ? prev : tempVarId));
  }

  /**
   * This function is for variant tab section edit button
   *               Tab
   * ______________________________________________
   * | checkbox   | variant name |     edit button |
   * |_____________________________________________|
   * while we click on edit button it will set the selectedCollapseKey
   */
  function onHandleEditButtonOnCollapse(tempCollapseKey, tempVarId) {
    setSelectedVariantId(tempVarId);
    setSelectedCollapseKey(tempCollapseKey);
  }

  function onHandleIngredientAdd(varId) {
    if (!isCategorySelected()) return;

    const variant = recipeVariants.find((el) => el.tempVarId === varId);
    const { ingredients } = variant;
    const updatedIngredients = [
      ...ingredients,
      {
        tempIngredientId: crypto.randomUUID(),
        ingredientId: "",
        ingredientName: "",
        quantity: 1,
        cost: "",
        baseUnit: "",
        recipeUnit: "",
        recipeUnitPrice: "",
        ingredientTotalCost: 0,
      },
    ];
    updateRecipeVariantField("variants", varId, "ingredients", updatedIngredients);
  }

  function onHandleIngredientRemove(varId, ingreId) {
    const variant = recipeVariants.find((el) => el.tempVarId === varId);
    const { ingredients } = variant;
    if (!Boolean(ingredients)) return;
    const updatedIngredients = [...ingredients];
    const index = updatedIngredients.findIndex((el) => el.tempIngredientId === ingreId);
    if (index !== -1) {
      updatedIngredients.splice(index, 1);
    }

    updateRecipeVariantField("variants", varId, "ingredients", updatedIngredients);
  }

  /*
   * This is a React useCallback hook that returns a memoized function.
   * It's used to update fields within a nested data structure representing a recipe,
   * which includes variants and their ingredients.
   *
   * @param {string} update - A string that determines what part of the data to update.
   * Can be "variants" or "ingredients".
   * @param {string} tempVariantId - The unique temporary ID of the recipe variant to be updated.
   * @param {string|string} tempIngredientIdOrFieldName - The temporary ID of the ingredient (if updating an ingredient)
   * OR the field name (if updating a variant).
   * @param {string|any} fieldNameOrValue - The field name (if updating an ingredient) OR the field value (if updating a variant).
   * @param {any} maybeValue - The field value (if updating an ingredient). This parameter is optional.
   *
   */
  const updateRecipeVariantField = useCallback(
    (update, tempVariantId, tempIngredientIdOrFieldName, fieldNameOrValue, maybeValue) => {
      // -------------------------------------------------------------
      // 1. Parameter Normalization
      // -------------------------------------------------------------

      let tempIngredientId, fieldName, fieldValue;

      // Adjust parameters based on the update type.
      // This allows the function to be called with different argument signatures
      // for updating a variant directly vs. updating an ingredient.
      if (update === "variants" && typeof maybeValue === "undefined") {
        // Scenario: Updating a variant field directly (e.g., variant name).
        // The third parameter is the field name, the fourth is the new value.
        tempIngredientId = null; // No ingredient ID needed for a variant update.
        fieldName = tempIngredientIdOrFieldName;
        fieldValue = fieldNameOrValue;
      } else {
        // Scenario: Updating an ingredient field (e.g., ingredient quantity).
        // The third parameter is the ingredient ID, the fourth is the field name,
        // and the fifth is the new value.
        tempIngredientId = tempIngredientIdOrFieldName;
        fieldName = fieldNameOrValue;
        fieldValue = maybeValue;
      }

      // -------------------------------------------------------------
      // 2. State Update Logic
      // -------------------------------------------------------------

      // Use the state setter function to update the `recipeVariants` state.
      // The previous state is passed as an argument (`prevRecipeVariants`).
      setRecipeVariants((prevRecipeVariants) =>
        // Iterate through each variant in the state array.
        prevRecipeVariants.map((variant) => {
          // If the current variant's ID does not match the target ID, return it unchanged.
          // This is a key part of maintaining immutability.
          if (variant?.tempVarId !== tempVariantId) return variant;

          // -------------------------------------------------------------
          // 3. Ingredient Update Logic
          // -------------------------------------------------------------

          // This block runs if we are targeting an ingredient.
          if (update === "ingredients" && tempIngredientId) {
            // Create a new array of ingredients by mapping over the existing ones.
            const updatedIngredients = variant.ingredients
              .map((ingredient) => {
                // If the current ingredient's ID does not match the target ID, return it unchanged.
                if (ingredient.tempIngredientId !== tempIngredientId) return ingredient;

                // Create a new, updated ingredient object using the spread syntax
                // to maintain immutability.
                const updatedIngredient = {
                  ...ingredient,
                  // Dynamically update the specified field with the new value.
                  [fieldName]: fieldValue,
                };

                // Check for duplicate ingredient names.
                // It uses the .some() method, which stops and returns true as soon as it finds a match.
                const isDuplicate = variant.ingredients.some((item) => {
                  // Skip the duplicate check if either the existing or the updated ingredient name is empty.
                  if (!item.ingredientName || !updatedIngredient.ingredientName) {
                    return false;
                  }

                  return (
                    // Ensure we are not comparing the ingredient against itself.
                    item.tempIngredientId !== tempIngredientId &&
                    // Check if the names are identical.
                    item.ingredientName === updatedIngredient.ingredientName
                  );
                });

                // If a duplicate is found, display a warning message to the user.
                if (isDuplicate) {
                  message.warning(
                    <p className="font-p_regular text-[12px]">
                      Your selected<span className="text-red-500"> ingredient item </span> is already exists. Please
                      just update the <span className="text-red-500">quantity</span>.
                    </p>
                  );
                  // Return null to signify this ingredient should not be included in the final list.
                  return null;
                }

                // If no duplicate is found, return the newly updated ingredient object.
                return updatedIngredient;
              })
              // Filter out any `null` values returned from the .map() operation,
              // which happens when a duplicate ingredient is found.
              .filter(Boolean);

            // Return a new variant object with the updated ingredients array.
            return {
              ...variant,
              ingredients: updatedIngredients,
            };
          }

          // -------------------------------------------------------------
          // 4. Variant Update Logic
          // -------------------------------------------------------------

          // This block runs if we are targeting a variant field directly.
          if (update === "variants") {
            // Return a new variant object with the specified field updated.
            return {
              ...variant,
              // Dynamically update the variant's field.
              [fieldName]: fieldValue,
            };
          }

          // -------------------------------------------------------------
          // 5. Default Case
          // -------------------------------------------------------------

          // If none of the conditions are met, return the variant unchanged.
          return variant;
        })
      );
    },
    // The dependency array for `useCallback`.
    // The function is only re-created if `setRecipeVariants` changes,
    // which it typically won't, preventing unnecessary re-renders.
    [setRecipeVariants]
  );

  const recipeTabAndItems = (Array.isArray(recipeVariants) ? recipeVariants : defaultVariantList).map(
    (variant, idx) => {
      if (!variant || Object.keys(variant).length < 1) return;
      return {
        label: (
          <div
            key={`variant-${idx}`}
            className={`${
              variantRecipeType !== INVENTORY_RECIPE_TYPE[0].value || menuCategory === "" ? "hidden" : "flex flex-col"
            } justify-between h-auto relative`}
          >
            <div className={`${!Boolean(menuCategory) ? "cursor-not-allowed" : "cursor-auto"}`}>
              <VariantInput
                updateRecipeVariantField={updateRecipeVariantField}
                index={idx}
                collapseActiveKey={selectedCollapseKey}
                variant={variant}
                handleTabChange={onHandleTabChange}
                handleCollapseEdit={onHandleEditButtonOnCollapse}
                setSelectedCollapseKey={setSelectedCollapseKey}
                setSelectedVariantId={setSelectedVariantId}
                recipeVariants={recipeVariants}
                setRecipeVariants={setRecipeVariants}
                firstTempVariantId={recipeVariants[0].tempVarId}
                isEdit={Boolean(recipeId)}
              />
            </div>
            {variant.tempVarId === selectedVariantId && (
              <div
                className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] rotate-90 absolute right-[-15px] top-[15px]"
                style={{
                  borderBottomColor: INVENTORY_VARIANT_TABS_BGCOLORS[idx % INVENTORY_VARIANT_TABS_BGCOLORS.length],
                }}
              ></div>
            )}
          </div>
        ),
        key: variant.tempVarId,
        children: (
          <RecipeVariantContainer
            isCategorySelected={Boolean(menuCategory)}
            updateRecipeVariantField={updateRecipeVariantField}
            allIngredients={ingredientItems}
            ingredientDropdownData={ingredientDropdown}
            currentRecipeVariant={variant}
            onAddIngredient={onHandleIngredientAdd}
            onRemoveIngredient={onHandleIngredientRemove}
            recipeReadyFoodType={recipeReadyFoodType}
          />
        ),
      };
    }
  );

  const deleteImage = async () => {
    const foodRecipeId = recipeId || "";
    try {
      if (!foodRecipeId) {
        setImages([]);
        setNewImage(null);
        return;
      }

      setNetworkCall(true);
      const token = user?.token?.token;
      const params = {
        context: "FOOD_EDIT",
        fieldName: "image",
        modelId: foodRecipeId || "",
      };
      const res = await ApiService.ADMIN_FILE_DELETE(params, token);

      if (res.code !== 200) {
        showNotification(res.data.message);
        return;
      }

      if (res.code === 200) {
        setImages([]);
        setNewImage(null);

        showNotification("Image deleted successfully.", false);
        return;
      }
    } catch (error) {
      console.error("Recipe image delete ==== ", error);
      showNotification(error.message || "An unexpected error occurred.");
    } finally {
      setNetworkCall(false);
    }
  };

  useEffect(() => {
    getCategories();
    if (ingredientItems.length < 1) {
      getIngredientItems();
      if (variantRecipeType !== INVENTORY_RECIPE_TYPE[1].value) {
        getAddOns();
      }
    }
  }, [restaurantId, variantRecipeType]);

  useEffect(() => {
    if (menuCategory) {
      getCategoryOptionsWithId(menuCategory, allCategory, data);
    }
  }, [menuCategory, allCategory]);

  return (
    <section className="px-[37px] pt-[30px] bg-white rounded-[12px]">
      <CustomLoader />
      <div className="flex items-center justify-between mb-[20px] space-x-2">
        <ConfigProvider
          theme={{
            components: {
              Select: {
                zIndexPopup: 1,
              },
            },
          }}
        >
          <div className="flex flex-col gap-y-3 mb-3">
            <div className="w-full h-auto flex items-center space-x-[22px]">
              <div className="h-[32px] min-w-[179px]">
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  Recipe Type <span className="text-red-600">*</span>
                </label>
                <Select
                  disabled={Boolean(recipeId)}
                  value={variantRecipeType}
                  name="recipeType"
                  style={{ width: "100%", height: "100%" }}
                  onChange={onHandleRecipeType}
                  options={INVENTORY_RECIPE_TYPE}
                  placeholder="Recipe type"
                />
              </div>

              <div className="h-[32px] min-w-[297px]">
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  Recipe name <span className="text-red-600">*</span>
                </label>
                <Input
                  name="recipeName"
                  className={"w-full h-full"}
                  value={recipeName}
                  onChange={onHandleRecipeName}
                  placeholder="Ex: Chicken Burger"
                />
              </div>

              <div className="h-[32px] min-w-[212px]">
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  Menu Category <span className="text-red-600">*</span>
                </label>
                <div className="w-100">
                  <Select
                    disabled={Boolean(recipeId)}
                    showSearch
                    value={menuCategory}
                    name="menuCategory"
                    style={{ width: "210px", height: "100%" }}
                    onChange={onHandleMenuCategory}
                    options={menuCategoryOptions}
                    placeholder="Ex: Appetizers"
                    optionFilterProp="label"
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())
                    }
                  />
                </div>
              </div>

              <div className="h-[32px]">
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  Food Type <span className="text-red-600">*</span>
                </label>
                <div className="w-100">
                  <Select
                    disabled={Boolean(recipeId)}
                    showSearch
                    value={recipeReadyFoodType}
                    name="recipeReadyFoodType"
                    style={{ width: "180px", height: "100%" }}
                    onChange={setRecipeReadyFoodType}
                    options={readyFoodTypes}
                    placeholder="Select"
                    optionFilterProp="label"
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())
                    }
                  />
                </div>
              </div>

              <div className={`h-[32px] ${recipeReadyFoodType !== 'RECIPE' ? '' : 'hidden'}`}>
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  Unit {recipeReadyFoodType !== 'RECIPE' && <span className="text-red-600">*</span> }
                </label>
                <div className="">
                  <Select
                    disabled={Boolean(recipeId)}
                    showSearch
                    value={ingredientUnit}
                    name="ingredientUnit"
                    style={{ width: "150px", height: "100%" }}
                    onChange={setIngredientUnit}
                    options={INVENTORY_INGREDIENT_UNIT}
                    placeholder="Select"
                    optionFilterProp="label"
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())
                    }
                  />
                </div>
              </div>
            </div>
            <div className="h-[32px] min-w-[297px] mt-5">
              <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">Food Description</label>
              <Input
                name="recipeFoodDescription"
                className={"w-full h-full"}
                value={recipeFoodDescription}
                onChange={onHandleRecipeFoodDescription}
                placeholder="Description"
              />
            </div>
          </div>
        </ConfigProvider>
        <div className="w-[120px] h-[120px]">
          <ImageUploadWithCropAndWebp
            fileList={images}
            setFileList={setImages}
            deleteImage={deleteImage}
            minWidth={500}
          />
        </div>
      </div>

      {/* variant */}
      <div ref={variantSectionRef} className="flex justify-center w-full" onClick={onHandleCheckMenuCategorySelected}>
        <Tabs
          defaultActiveKey={recipeVariants[0].tempVarId}
          activeKey={selectedVariantId || recipeVariants[0]?.tempVarId}
          tabBarExtraContent={
            <button
              disabled={variantRecipeType !== INVENTORY_RECIPE_TYPE[0].value}
              className={`w-full my-[20px] ${
                variantRecipeType !== INVENTORY_RECIPE_TYPE[0].value ? "bg-[#DDE7B3]" : "bg-[#C5DF5E]"
              } h-[42px] flex items-center justify-between pl-[41px] pr-[35px]`}
              onClick={onHandleAddVariant}
            >
              <span className="font-bold text-sm leading-[16px] text-black">Add Variants</span>
              <Image
                src="https://tabletap-cdn.s3.ap-south-1.amazonaws.com/tabletap-prod/static-assets/add2.svg"
                preview={false}
                alt="add-variant"
              />
            </button>
          }
          className="w-full min-h-[424px] inventory-recipe-variant"
          tabPosition="left"
          items={recipeTabAndItems}
        />
      </div>
      {/* variant ends */}
      <div
        className="ml-[260px] mt-[25px] flex items-center justify-between"
        style={
          Boolean(menuCategory)
            ? { pointerEvents: "auto", cursor: "default" }
            : { pointerEvents: "none", cursor: "not-allowed" }
        }
      >
        {variantRecipeType === "FOOD"  && recipeReadyFoodType !== 'INGREDIENT' && (
          <div className="mt-2">
            <button
              className="w-[204px] h-[42px] rounded-[5px] bg-[#2EC114] text-sm font-bold text-white"
              onClick={onHandleOpenAddOnsModal}
            >
              Select Add-Ons
            </button>
          </div>
        )}
        {  recipeReadyFoodType !== 'INGREDIENT' && 
        <div className="flex items-center space-x-[5px]">
          <div className="h-[32px] w-[312px] mb-[35px]">
            <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">Discount</label>
            {/* <Input
              type="number"
              min={0}
              max={100}
              name="disount"
              className="w-full h-full"
              value={variantDiscount}
              onChange={onHandleDiscount}
            /> */}
            <CustomInputNumber
              value={variantDiscount}
              setValue={onHandleDiscount}
              min={0}
              max={discountType === "PERCENT" ? 100 : 99999}
              isDecimalAllowed={true}
            />
          </div>
          <div className="w-[83px] h-[32px] mt-2.5">
            <Select
              value={discountType}
              name="profitType"
              style={{ width: "100%", height: "100%" }}
              onChange={onHandleDiscountType}
              options={INVENTORY_ADUSTMENT_TYPE_UNIT}
            />
          </div>
        </div>
        }
      </div>

      {/* show selected addons */}
      {selectedAddOnsNames.length > 0 && (
        <div className="drop-shadow-sm ml-[260px] mt-5 text-black pl-3 py-3 text-[14px] font-p_medium border border-px border-[#E2E2E2] rounded-md">
          {selectedAddOnsNames.map((el, idx) => (idx === selectedAddOnsNames.length - 1 ? el : `${el}, `))}
        </div>
      )}
      {/* end show selected addons */}

      {/*Meal Addition Section*/}
      {variantRecipeType === "FOOD" && recipeReadyFoodType !== 'INGREDIENT' && (
        <div className="ml-[260px] mt-[25px]">
          <FoodMealAddition foodAdditionGroups={foodAdditionGroups} setFoodAdditionGroups={setFoodAdditionGroups} />
        </div>
      )}

      {/*Food Tax And Discount*/}
      {variantRecipeType === "FOOD" && recipeReadyFoodType !== 'INGREDIENT'  && (
        <div className="ml-[260px] mt-[25px]">
          <FoodTaxAndDiscount
            records={{
              isVatApplicable,
              setIsVatApplicable,
              isDiscountApplicable,
              setIsDiscountApplicable,
              isSupplementaryDutyApplicable,
              setIsSupplementaryDutyApplicable,
              supplementaryDuty,
              setSupplementaryDuty,
            }}
          />
        </div>
      )}

      <div className="flex items-center space-x-[15px] pl-[calc(20%+20px)] mt-[44px] pb-[70px]">
        <button
          className="text-base font-p_medium leading-[13px] rounded-[5px] text-black bg-[#FCBD10] h-[42px] w-[100px]"
          onClick={() => navigate("/inventory/recipe")}
        >
          Cancel
        </button>
        <button
          className="text-base font-p_medium leading-[13px] rounded-[5px] text-white bg-black h-[42px] w-[100px]"
          onClick={onHandleUpsert}
        >
          {!Boolean(recipeId) ? "Save" : "Update"}
        </button>
      </div>

      <RecipeFoodAddOnsModal
        open={openAddOnsModal}
        setOpenModal={setOpenAddOnsModal}
        setSelectedAddOns={setSelectedAddOns}
        addOns={variantAddOns}
        updateAddOns={updateAddOns}
        handleOk={onHandleOpenAddOnsModal}
        handleAddAddOns={onHandleAddAddOnsItem}
        handleRemoveAddOns={onHandleRemoveAddOnsItem}
        handleSaveAddOn={onHandleSaveAddOns}
        handleCancelAddOn={onHandleCancelAddOns}
      />
      <RecipeEditExceptionModal
        open={openEditExceptionModal}
        title="This recipe is linked to active orders or set menus. To update it, wait until the orders are completed or remove it from the set menu."
        dependentSetMenuOrders={dependentSetmenuOrders}
        dependentSetMenus={dependentSetmenus}
        dependentOrders={dependentOrders}
        onConfirm={onHandleEditExceptionModalOkBtn}
        onCancel={onHandleEditExceptionModalCancelBtn}
      />
    </section>
  );
}
